import { Code2, Palette, Sparkles, PenTool, Globe } from "lucide-react";
import { Reveal } from "./Reveal";
import { PaintSplat } from "./PaintSplat";
import { playTick } from "../../lib/sound";

const SERVICES = [
  {
    icon: Code2,
    title: "Software Development",
    body: "Full-stack web & mobile apps, from architecture to launch. Clean code, real performance.",
    span: "md:col-span-7",
    tags: ["React", "FastAPI", "Mobile"],
    accent: "#2dd4bf",
    wash: "#0d5f57",
    image: "https://images.unsplash.com/photo-1489875347897-49f64b51c1f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxzb2Z0d2FyZSUyMGRldmVsb3BlciUyMGNvZGUlMjBzY3JlZW4lMjBkYXJrfGVufDB8fHx8MTc4NzQwNTIxOHww&ixlib=rb-4.1.0&q=85",
    alt: "Software development — code on screen",
  },
  {
    icon: Palette,
    title: "Brand Identity",
    body: "Logos, systems and guidelines that give your product a voice.",
    span: "md:col-span-5",
    tags: ["Logo", "Systems"],
    accent: "#ff6b6b",
    wash: "#6e2a28",
    image: "https://images.unsplash.com/photo-1781615851923-2f1e05d7abfa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxicmFuZCUyMGlkZW50aXR5JTIwZGVzaWduJTIwc3R1ZGlvfGVufDB8fHx8MTc4NzQwNTIxOHww&ixlib=rb-4.1.0&q=85",
    alt: "Brand identity — studio signage",
  },
  {
    icon: PenTool,
    title: "Graphic Design",
    body: "Editorial, print & digital collateral with a distinctive hand-crafted edge.",
    span: "md:col-span-5",
    tags: ["Print", "Editorial"],
    accent: "#f9803e",
    wash: "#6e3711",
    image: "https://images.pexels.com/photos/8824026/pexels-photo-8824026.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Graphic design — editorial print layout",
  },
  {
    icon: Globe,
    title: "Website Development",
    body: "Fast, responsive websites built to convert — from marketing sites to full CMS and e-commerce.",
    span: "md:col-span-7",
    tags: ["Responsive", "CMS", "SEO", "E-commerce"],
    accent: "#fbbf24",
    wash: "#6b5115",
    image: "https://images.pexels.com/photos/34140/pexels-photo.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Website development — responsive across devices",
  },
];

export const Services = () => (
  <section id="services" className="relative py-28 md:py-40 overflow-hidden" data-testid="services-section">
    <PaintSplat color="orange2" className="w-[440px] h-[440px] -top-14 -left-20" rotate={-10} opacity={0.46} from={80} to={-80} />
    <PaintSplat color="teal3" className="w-[420px] h-[420px] -bottom-16 -right-20" rotate={200} opacity={0.5} from={-60} to={60} flip />
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 relative z-10">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-24">
        <div className="max-w-2xl">
          <span className="font-mono-accent text-[11px] tracking-[0.3em] uppercase text-[#8b8b8b]">
            [ 01 ] &nbsp;What We Do
          </span>
          <h2 className="font-display font-medium tracking-tighter text-4xl md:text-6xl mt-6 leading-[1.02]">
            Capabilities under one roof.
          </h2>
        </div>
        <Sparkles className="text-gold hidden md:block" size={36} />
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <Reveal
              key={s.title}
              delay={i * 0.07}
              onMouseEnter={playTick}
              style={{ "--accent": s.accent }}
              className={`${s.span} svc-card group relative overflow-hidden rounded-2xl border border-[#242424] bg-[#0f0f11] min-h-[240px]`}
              data-testid={`service-card-${i}`}
            >
              <div className="flex flex-col sm:flex-row h-full">
                {/* Text content */}
                <div className="relative flex flex-col flex-1 p-8 md:p-10">
                  <div className="relative flex items-center justify-between">
                    <Icon size={30} className="svc-icon text-[#eaeaea]" strokeWidth={1.5} />
                    <span className="font-mono-accent text-[11px] text-[#4a4a4a]">0{i + 1}</span>
                  </div>
                  <div className="relative mt-8">
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-3">{s.title}</h3>
                    <p className="text-[#9a9a9a] text-sm md:text-base leading-relaxed max-w-md mb-5">{s.body}</p>
                    <div className="flex flex-wrap gap-2">
                      {s.tags.map((t) => (
                        <span key={t} className="font-mono-accent text-[10px] uppercase tracking-[0.15em] text-[#8b8b8b] border border-[#2a2a2a] rounded-full px-3 py-1">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Showcase image — duotone that blooms to full color on hover */}
                <div className="relative w-full sm:w-[42%] md:w-[44%] shrink-0 min-h-[180px] sm:min-h-full overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.alt}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover grayscale contrast-[1.08] brightness-[0.72] scale-[1.04] transition-all duration-[900ms] ease-out group-hover:grayscale-0 group-hover:brightness-100 group-hover:scale-100"
                    data-testid={`service-image-${i}`}
                  />
                  {/* per-capability duotone wash — fades out on hover */}
                  <div
                    className="absolute inset-0 mix-blend-color opacity-70 transition-opacity duration-[900ms] ease-out group-hover:opacity-0"
                    style={{ backgroundColor: s.wash }}
                    aria-hidden="true"
                  />
                  {/* edge fade so the image melts into the card — top-fade on mobile, left-fade on desktop */}
                  <div
                    className="absolute inset-0 pointer-events-none sm:hidden"
                    style={{ background: "linear-gradient(180deg, #0f0f11 0%, rgba(15,15,17,0.3) 30%, rgba(15,15,17,0) 60%)" }}
                    aria-hidden="true"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none hidden sm:block"
                    style={{ background: "linear-gradient(90deg, #0f0f11 0%, rgba(15,15,17,0.35) 22%, rgba(15,15,17,0) 55%)" }}
                    aria-hidden="true"
                  />
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
